import type { Middleware } from "redux";
import { z } from "zod";
import toast from "react-hot-toast";
import type { IError } from "entities/common";
import type { AppQueryConfig } from "QueryTypes";

interface ZodValidationConfig {
  schema: z.ZodType<any> | z.ZodType<any>[];
  entityName: string;
  useIndividualValidation?: boolean; // For cases like events where you validate each item
  use207Processing?: boolean; // Whether to use your 207 response processing
}

// Create a custom type that extends the existing QueryConfig
// This avoids the type parameter mismatch issue
interface QueryConfigWithZodValidation extends Record<string, any> {
  zodValidation?: ZodValidationConfig;
}

// Type helper for your queries
export type AppQueryConfigWithZod = AppQueryConfig &
  QueryConfigWithZodValidation;

/**
 * Process 207 responses with errors
 */
function process207Response<T>(data: any, entityName: string): T[] {
  //console.debug('🔍 207 PROCESSING: Raw data received:', data);
  //console.debug('🔍 207 PROCESSING: Data type:', typeof data, 'Has errors:', 'errors' in data);

  if (data && typeof data === "object" && "errors" in data) {
    const responseData = data;
    //console.debug('🔍 207 PROCESSING: Found errors in response:', responseData.errors);
    //console.debug('🔍 207 PROCESSING: Results in response:', responseData.results);

    // Display error toasts based on the number of errors
    if (responseData.errors && Array.isArray(responseData.errors)) {
      const errors = responseData.errors;
      //console.debug('🚨 207 PROCESSING: Found errors:', errors);

      if (errors.length > 0 && errors.length <= 3) {
        // Show specific errors for 1-3 errors
        errors.forEach((error: IError) => {
          //console.debug('🚨 207 ERROR:', error);
          toast(error.message || "Unknown error", {
            duration: 4000,
            icon: "⚠️",
          });
        });
      } else if (errors.length > 3) {
        // Generic message for more than 3 errors
        //console.debug('🚨 207 PROCESSING: Too many errors to display individually:', errors.length);

        toast(
          `${
            errors.length
          } errors occurred while retrieving ${entityName} - ${errors
            .map((error: IError) => error.message)
            .join(", ")}`,
          { duration: 4000, icon: "⚠️" },
        );
      }
    }
    const results = responseData.results || responseData.result || [];
    /*
    console.debug('🔍 207 PROCESSING: Returning results:', results);
    console.debug('🔍 207 PROCESSING: Individual result details:');
    results.forEach((result: any, index: number) => {
      console.debug(`  Result ${index}:`, {
        eventId: result?.eventId,
        eventResultId: result?.eventResultId,
        player: result?.player?.name || result?.player?.displayName,
        place: result?.place || result?.placeDisplay,
        hasEventId: !!result?.eventId
      });
    });
    */
    return results;
  }
  //console.log('🔍 207 PROCESSING: No 207 structure found, returning data as-is:', data);
  return data;
}

/**
 * Normalize schema to an array
 */
function normalizeSchemas(
  schema: z.ZodType<any> | z.ZodType<any>[],
): z.ZodType<any>[] {
  return Array.isArray(schema) ? schema : [schema];
}

/**
 * Try multiple schemas against a single item until one succeeds
 */
function validateWithSchemas(item: any, schemas: z.ZodType<any>[]) {
  for (let i = 0; i < schemas.length; i++) {
    const currentSchema = schemas[i];
    const result = currentSchema?.safeParse(item);

    if (result?.success) {
      //console.trace(`✅ ZOD VALIDATION: ${context} validated with schema ${i}:`, item);
      return { success: true, data: result.data, schemaIndex: i };
    } else {
      //console.debug(`🔍 ZOD VALIDATION: ${context} failed schema ${i}:`, item);
    }
  }

  return { success: false, data: null, schemaIndex: -1 };
}

/**
 * Log validation failure details
 */
function logValidationFailure(item: any, context: string) {
  console.warn(`🚨 ZOD VALIDATION: ${context} failed all schemas:`, {
    eventId: item?.id,
    eventResultId: item?.eventResultId,
    tournamentResultId: item?.tournamentResultId,
    startTime: item?.startTime,
    endTime: item?.endTime,
    startTimeType: typeof item?.startTime,
    endTimeType: typeof item?.endTime,
    rawItem: item,
  });
}

export const zodValidationMiddleware: Middleware = () => (next) => (action) => {
  // We need to intercept BEFORE Redux-Query processes the response
  // Look for the request action that Redux-Query dispatches when a request completes
  // For queries: @@query/REQUEST_ASYNC, For mutations: @@query/MUTATE_ASYNC
  if (
    action.type === "@@query/REQUEST_ASYNC" ||
    action.type === "@@query/MUTATE_ASYNC"
  ) {
    /*console.trace(`🚀 Redux-Query ${action.type === '@@query/REQUEST_ASYNC' ? 'REQUEST' : 'MUTATE'} ASYNC action:`, {
      type: action.type,
      queryKey: action.queryKey,
      actionKeys: Object.keys(action)
    });*/

    // Check if this query has Zod validation configured
    const zodValidation = getZodValidationFromQueryKey(action.queryKey);

    if (zodValidation) {
      // We need to wrap the original transform function
      const originalTransform = action.transform;

      action.transform = (responseData: any) => {
        const {
          schema,
          entityName,
          useIndividualValidation,
          use207Processing,
        } = zodValidation;

        try {
          let dataToValidate = responseData;

          // Apply 207 processing if requested
          if (use207Processing) {
            dataToValidate = process207Response(responseData, entityName);
            console.debug("📊 After 207 processing:", dataToValidate);
          }

          const allSchemas = normalizeSchemas(schema);
          let validatedData;

          if (useIndividualValidation) {
            // Individual validation - validate each item in the array
            const validItems: any[] = [];
            const invalidItems: any[] = [];

            //console.trace(`🔍 ZOD VALIDATION: Starting individual validation for ${dataToValidate.length} items with ${allSchemas.length} schemas`);

            for (const item of dataToValidate) {
              const result = validateWithSchemas(item, allSchemas);

              if (result.success) {
                validItems.push(result.data);
              } else {
                logValidationFailure(item, "Item");
                invalidItems.push(item);
              }
            }

            console.debug(
              `🔍 ZOD VALIDATION: Results - ${validItems.length} valid, ${invalidItems.length} invalid`,
            );
            if (invalidItems.length > 0) {
              console.warn(
                `🚨 ZOD VALIDATION (${entityName}): ${invalidItems.length} items failed validation and will be excluded`,
              );
            }

            validatedData = validItems;
          } else {
            // Single object validation - validate the response as a single object
            console.debug(
              `🔍 ZOD VALIDATION: Starting single object validation for ${entityName} with ${allSchemas.length} schemas`,
            );
            console.debug(
              `🔍 ZOD VALIDATION: Data to validate:`,
              dataToValidate,
            );

            const result = validateWithSchemas(
              dataToValidate,
              allSchemas,
            );

            if (result.success) {
              console.debug(
                `✅ ZOD VALIDATION: Single object validation successful for ${entityName}`,
              );
              validatedData = result.data;
            } else {
              console.error(
                `🚨 ZOD VALIDATION: Single object validation failed for ${entityName} with all schemas`,
              );
              console.error(
                `🚨 ZOD VALIDATION: Raw data that failed:`,
                dataToValidate,
              );
              toast.error(`Invalid ${entityName} data received`, {
                duration: 4000,
              });
              validatedData = null; // Return null on validation failure for single objects
            }
          }

          // Now call the original transform function with validated data
          if (originalTransform && typeof originalTransform === "function") {
            const transformResult = originalTransform(validatedData);
            return transformResult;
          } else {
            return validatedData;
          }
        } catch (error) {
          console.error(`Zod validation error for ${entityName}:`, error);
          // Fall back to original transform on error
          if (originalTransform && typeof originalTransform === "function") {
            return originalTransform(responseData);
          }
          return responseData;
        }
      };
    }
  }

  // Log other Redux-Query actions for debugging
  /*if (action.type === '@@query/REQUEST_SUCCESS') {
    console.log('✅ Redux-Query SUCCESS action:', {
      type: action.type,
      queryKey: action.queryKey,
      body: action.body,
      bodyType: typeof action.body,
      bodyKeys: Object.keys(action.body || {}),
      entities: action.entities,
      entitiesType: typeof action.entities,
      entitiesKeys: Object.keys(action.entities || {}),
      meta: action.meta,
      actionKeys: Object.keys(action),
    });
    
    // Log more details about entities
    if (action.entities && typeof action.entities === 'object') {
      console.log('🔍 SUCCESS entities detail:', action.entities);
      Object.keys(action.entities).forEach(entityKey => {
        console.log(`🔍 Entity ${entityKey}:`, typeof action.entities[entityKey], Object.keys(action.entities[entityKey] || {}));
      });
    }
  }*/

  return next(action);
};

// Helper function to match query keys to validation configs
const queryValidationRegistry = new Map();

export const registerQueryValidation = (
  queryKey: string,
  zodValidation: ZodValidationConfig,
) => {
  queryValidationRegistry.set(queryKey, zodValidation);
};

const getZodValidationFromQueryKey = (queryKey: string) => {
  const validation = queryValidationRegistry.get(queryKey);
  if (process.env.NODE_ENV !== "production") {
    console.debug(
      "🔍 Looking up validation for query:",
      queryKey,
      "Found:",
      !!validation,
    );
  }
  return validation;
};
